import { z } from 'zod';
import { ASSESSMENT_STATUSES } from './portfolio.js';
import { can } from './rbac.js';
/**
 * Messages (tracker task 65, decision 0039). Each assessment has one thread between the insurer, the
 * broker and the business: messages, comments on single questions, internal notes only the insurer sees,
 * and entries the platform writes when changes are asked for, answered or a decision is made. People
 * involved get an email notice linking to the thread; each insurer can change the wording of its notices.
 */
/** People who read internal notes are "the insurer" in a thread (task 78: by permission, not role). */
export const isInsurer = (perms) => can(perms, 'messages.internal_view'); // prettier-ignore
export const MESSAGE_MAX = 4000;
export const PostMessage = z.strictObject({
    body: z.string().trim().min(1).max(MESSAGE_MAX),
    /** A comment on one question; without it, a message in the general thread. */
    question: z
        .string()
        .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/)
        .optional(),
    /** Only the insurer sees it, and nobody is emailed. */
    internal: z.boolean().default(false),
});
/** The insurer reads internal notes; a broker starts checks for the businesses it places; the rest answer. */
export const sideOf = (perms) => isInsurer(perms) ? 'insurer' : can(perms, 'assessments.create') ? 'broker' : 'business';
/** What a platform entry is about. */
export const SYSTEM_EVENTS = ['changes_requested', 'change_replied', 'changes_returned', 'decided', 'condition_evidenced', 'condition_reviewed']; // prettier-ignore
/** How many conversations the list shows. */
export const CONVERSATIONS_MAX = 200;
export const CONVERSATION_SHOW = ['all', 'unread', 'waiting'];
export const CONVERSATION_SORT = ['recent', 'oldest', 'unread', 'client'];
/**
 * Narrowing the list (the Messages page's filters): one client, a search through what was written and who
 * it is about, entries from one moment up to (not including) another (the page sends the viewer's own
 * days), the assessment's status, only unread or only those waiting on a reply, and the order.
 */
export const ConversationsQuery = z
    .strictObject({
    client: z.string().trim().min(1).max(200).optional(),
    q: z.string().trim().min(1).max(200).optional(),
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
    status: z.enum(ASSESSMENT_STATUSES).optional(),
    show: z.enum(CONVERSATION_SHOW).default('all'),
    sort: z.enum(CONVERSATION_SORT).default('recent'),
})
    .refine((q) => !q.from || !q.to || Date.parse(q.from) <= Date.parse(q.to), { message: '"from" must not be after "to"', path: ['from'] }); // prettier-ignore
// —— email notices ——
export const NOTICE_KEYS = ['message', 'changes_requested', 'changes_returned', 'decision'];
/** {count} reads as a number of answers, e.g. "1 answer" or "3 answers". */
export const NOTICE_PLACEHOLDERS = ['business', 'insurer', 'author', 'count'];
export const answersCount = (n) => `${n} ${n === 1 ? 'answer' : 'answers'}`;
export const NOTICE_DEFAULTS = {
    message: {
        label: 'New message',
        when: 'Someone writes in an assessment’s thread or comments on a question.',
        subject: 'New message about {business}’s Cyber Check',
        body: '{author} wrote about {business}’s Cyber Check:',
    },
    changes_requested: {
        label: 'Changes requested',
        when: 'The insurer sends change requests to the business.',
        subject: '{insurer} asked for changes to your Cyber Check',
        body: '{insurer} reviewed your Cyber Check and asked you to look at {count} again. Change each one, or reply to say why it’s right, then send it back.',
    },
    changes_returned: {
        label: 'Changes sent back',
        when: 'The business sends its changes back to the insurer.',
        subject: '{business} sent back its Cyber Check',
        body: '{business} answered the changes you asked for ({count}). It is back with you to review.',
    },
    decision: {
        label: 'Decision made',
        when: 'The insurer records a decision the business is told about.',
        subject: '{insurer} has decided on your Cyber Check',
        body: '{insurer} recorded its decision on your Cyber Check. Open it to see the outcome and any conditions.',
    },
};
const PLACEHOLDER = /\{([a-z_]+)\}/g;
/** Placeholders a wording uses that aren't known. */
export function unknownPlaceholders(text) {
    return [...text.matchAll(PLACEHOLDER)].map((m) => m[1]).filter((p) => !NOTICE_PLACEHOLDERS.includes(p)); // prettier-ignore
}
const wordingText = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((t) => !/[\r\n]/.test(t) || max > 150, 'One line')
    .superRefine((t, ctx) => {
    const bad = unknownPlaceholders(t);
    if (bad.length)
        ctx.addIssue({ code: 'custom', message: `Unknown placeholder ${bad.map((b) => `{${b}}`).join(', ')}; use ${NOTICE_PLACEHOLDERS.map((p) => `{${p}}`).join(', ')}` }); // prettier-ignore
});
export const NoticeWordingInput = z.strictObject({ subject: wordingText(150), body: wordingText(1000) });
/** Fills placeholders; one with no value is left out. */
export function renderNotice(w, vars) {
    const fill = (t) => t.replace(PLACEHOLDER, (_, k) => String(vars[k] ?? '')).replace(/\s{2,}/g, ' ').trim(); // prettier-ignore
    return { subject: fill(w.subject), body: fill(w.body) };
}
//# sourceMappingURL=messages.js.map